'use client';

import { plainDashes } from '@slims/shared/data';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import { type ScopeLeaf, useScopeTree } from '@/hooks/use-scope-tree';
import { useSelectedScopes } from '@/hooks/use-selected-scopes';
import { hasDocumentFile } from '@/lib/document-files';
import { subscribeToTable } from '@/lib/supabase/realtime-registry';
import { api } from '@/lib/utils/api';
import { useAuthStore } from '@/stores/auth-store';

export interface DBNotification {
	id: string;
	tenantId: string;
	memberId: string | null;
	direction: 'inbound' | 'outbound';
	/** Mirrors the `notification_type` enum in Postgres. */
	tipo:
		| 'expiry_alert'
		| 'missing_documents'
		| 'pod_anomaly'
		| 'monthly_report'
		| 'weekly_summary'
		| 'manual'
		| 'system'
		| 'deadline_acconto'
		| 'deadline_conguaglio'
		| 'deadline_rendicontazione'
		| 'deadline_pnrr'
		| 'deadline_pnrr_saldo'
		| 'deadline_domanda_gse'
		| 'deadline_integration_scaduta'
		| 'deadline_antimafia'
		| 'antimafia_threshold'
		| 'live_feed_down'
		| 'forecast_accuracy_low'
		| 'consumption_anomaly'
		| 'wallet_soglia';
	titolo: string;
	messaggio: string | null;
	letto: boolean;
	canale: string | null;
	allegatoPath: string | null;
	allegatoNome: string | null;
	createdAt: string | null;
	recipientRole: string;
	requestId: string | null;
	delivery?: {
		email: { requested: number; sent: number; skipped: number; failed: number };
	};
}

interface UseNotificationsReturn {
	notifications: DBNotification[];
	isLoading: boolean;
	error: string | null;
	unreadCount: number;
	refetch: () => Promise<void>;
	markAsRead: (id: string) => Promise<void>;
	markAsUnread: (id: string) => Promise<void>;
	markAllAsRead: () => Promise<void>;
	createNotification: (
		data: Omit<DBNotification, 'id' | 'tenantId' | 'createdAt'>,
		orgId?: string,
	) => Promise<DBNotification>;
	deleteNotification: (id: string) => Promise<void>;
	deleteNotifications: (ids?: string[]) => Promise<void>;
	/** Regenerate the sample notification set for one org (admin only). */
	generateTestNotifications: (opts?: { orgId?: string; memberId?: string | null }) => Promise<number>;
	/** Purge the generated sample set for one org (admin only). */
	deleteTestNotifications: (orgId?: string) => Promise<number>;
}

/** `canale` marker the API writes on generated samples — kept in sync with the API. */
export const TEST_NOTIFICATION_CANALE = 'test';

const EMPTY: DBNotification[] = [];

function sortByCreated(rows: DBNotification[]): DBNotification[] {
	return rows.sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));
}

// Supabase Realtime delivers snake_case column names; normalise to camelCase.
function normaliseRow(raw: Record<string, unknown>): DBNotification {
	return {
		id: raw.id as string,
		tenantId: (raw.tenant_id ?? raw.tenantId) as string,
		memberId: (raw.member_id ?? raw.memberId ?? null) as string | null,
		direction: raw.direction as DBNotification['direction'],
		tipo: raw.tipo as DBNotification['tipo'],
		// Le notifiche salvate prima del cambio hanno ancora «—» (vedi `plainDashes`).
		titolo: plainDashes(String(raw.titolo ?? '')),
		messaggio: typeof raw.messaggio === 'string' ? plainDashes(raw.messaggio) : null,
		letto: raw.letto as boolean,
		canale: (raw.canale ?? null) as string | null,
		allegatoPath: (raw.allegato_path ?? raw.allegatoPath ?? null) as string | null,
		allegatoNome: (raw.allegato_nome ?? raw.allegatoNome ?? null) as string | null,
		createdAt: (raw.created_at ?? raw.createdAt ?? null) as string | null,
		recipientRole: (raw.recipient_role ?? raw.recipientRole) as string,
		requestId: (raw.request_id ?? raw.requestId ?? null) as string | null,
	};
}

/**
 * Notifications across the current view selection: one fetch per selected
 * configuration, merged by createdAt. Item mutations resolve the row's org from
 * its tenantId via the scope tree.
 *
 * ADR-006: backed by React Query, chiave `['notifications', scopeKey]` — tutte
 * le istanze (badge header, pagina, sheet) condividono un fetch e una cache, e
 * un `setQueryData` le aggiorna tutte. I canali realtime (uno per tenant)
 * applicano le patch alla cache.
 */
export function useNotifications(options?: {
	/** Admin-area override: scope to this org only, ignoring the view selection. */
	orgId?: string | null;
}): UseNotificationsReturn {
	const queryClient = useQueryClient();
	const { leaves } = useScopeTree();
	const { scopes } = useSelectedScopes();
	const role = useAuthStore((s) => s.role);

	const overrideOrgId = options?.orgId ?? null;

	// Stable scope key so effects/queries don't loop on array identity.
	const scopeKey = overrideOrgId ?? scopes.map((s) => s.orgId).join(',');

	const targetScopes: ScopeLeaf[] = useMemo(() => {
		if (overrideOrgId) return leaves.filter((l) => l.orgId === overrideOrgId);
		const ids = scopeKey ? scopeKey.split(',') : [];
		const byId = new Map(leaves.map((l) => [l.orgId, l]));
		return ids.map((id) => byId.get(id)).filter((l): l is ScopeLeaf => Boolean(l));
	}, [scopeKey, leaves, overrideOrgId]);

	const { data, isLoading, error, refetch: rqRefetch } = useQuery({
		queryKey: ['notifications', scopeKey],
		enabled: targetScopes.length > 0,
		queryFn: async () => {
			const results = await Promise.all(
				targetScopes.map((scope) =>
					api.get<DBNotification[]>(`/api/notifications?orgId=${scope.orgId}`),
				),
			);
			return sortByCreated(results.flat());
		},
	});

	const notifications = Array.isArray(data) ? data : EMPTY;

	const refetch = useCallback(async () => {
		await rqRefetch();
	}, [rqRefetch]);

	const patchCache = useCallback(
		(update: (prev: DBNotification[]) => DBNotification[]) => {
			queryClient.setQueryData<DBNotification[]>(['notifications', scopeKey], (prev) =>
				update(prev ?? []),
			);
		},
		[queryClient, scopeKey],
	);

	/** orgId of a row, resolved from its tenantId (tenant ids are unique per org). */
	const orgIdForRow = useCallback(
		(id: string): string | null => {
			const row = notifications.find((n) => n.id === id);
			if (!row) return null;
			return leaves.find((l) => l.tenantId === row.tenantId)?.orgId ?? null;
		},
		[notifications, leaves],
	);

	// Cross-instance sync via window events — kept for external listeners; with a
	// shared query cache the same-key instances already update together.
	useEffect(() => {
		const onRead = (e: Event) => {
			const { id } = (e as CustomEvent<{ id: string }>).detail;
			patchCache((prev) => prev.map((n) => (n.id === id ? { ...n, letto: true } : n)));
		};
		const onUnread = (e: Event) => {
			const { id } = (e as CustomEvent<{ id: string }>).detail;
			patchCache((prev) => prev.map((n) => (n.id === id ? { ...n, letto: false } : n)));
		};
		const onReadAll = () => {
			patchCache((prev) => prev.map((n) => ({ ...n, letto: true })));
		};
		window.addEventListener('notif:read', onRead);
		window.addEventListener('notif:unread', onUnread);
		window.addEventListener('notif:read-all', onReadAll);
		return () => {
			window.removeEventListener('notif:read', onRead);
			window.removeEventListener('notif:unread', onUnread);
			window.removeEventListener('notif:read-all', onReadAll);
		};
	}, [patchCache]);

	// Supabase Realtime subscription — one per selected tenant.
	useEffect(() => {
		if (targetScopes.length === 0) return;
		// Members see only their own notifications (server-filtered by recipientRole + memberId).
		// Re-fetching on INSERT lets the server apply that filter, preventing members from
		// receiving admin-targeted or other members' notifications via realtime.
		const isMemberView = role === 'member';

		const unsubs = targetScopes.map((scope) =>
			subscribeToTable(
				`notifications-realtime-${scope.tenantId}`,
				{
					event: '*',
					schema: 'public',
					table: 'notifications',
					filter: `tenant_id=eq.${scope.tenantId}`,
				},
				(payload) => {
					// Con un allegato del bucket `documents` la riga porta il riferimento
					// non firmato: si rilegge dall'API, che lo firma (INB-379).
					const raw = payload.new as { allegato_path?: unknown; attachments?: unknown };
					if (
						(payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') &&
						hasDocumentFile(raw.allegato_path, raw.attachments)
					) {
						void rqRefetch();
					} else if (payload.eventType === 'INSERT') {
						const inserted = normaliseRow(payload.new);
						if (isMemberView && inserted.recipientRole === 'cer_user') {
							// Message from admin → member: refetch so server applies member filter.
							void rqRefetch();
						} else {
							patchCache((prev) =>
								prev.some((n) => n.id === inserted.id)
									? prev
									: sortByCreated([inserted, ...prev]),
							);
						}
					} else if (payload.eventType === 'UPDATE') {
						const updated = normaliseRow(payload.new);
						patchCache((prev) => prev.map((n) => (n.id === updated.id ? updated : n)));
					} else if (payload.eventType === 'DELETE') {
						const removedId = (payload.old as { id: string }).id;
						patchCache((prev) => prev.filter((n) => n.id !== removedId));
					}
				},
			),
		);
		return () => {
			for (const unsub of unsubs) unsub();
		};
	}, [scopeKey, role, targetScopes, patchCache, rqRefetch]);

	const markAsRead = useCallback(
		async (id: string) => {
			const orgId = orgIdForRow(id);
			if (!orgId) return;
			await api.patch(`/api/notifications/${id}?orgId=${orgId}`, { letto: true });
			patchCache((prev) => prev.map((n) => (n.id === id ? { ...n, letto: true } : n)));
			window.dispatchEvent(new CustomEvent('notif:read', { detail: { id } }));
		},
		[orgIdForRow, patchCache],
	);

	const markAsUnread = useCallback(
		async (id: string) => {
			const orgId = orgIdForRow(id);
			if (!orgId) return;
			await api.patch(`/api/notifications/${id}?orgId=${orgId}`, { letto: false });
			patchCache((prev) => prev.map((n) => (n.id === id ? { ...n, letto: false } : n)));
			window.dispatchEvent(new CustomEvent('notif:unread', { detail: { id } }));
		},
		[orgIdForRow, patchCache],
	);

	const markAllAsRead = useCallback(async () => {
		const unread = notifications.filter((n) => !n.letto);
		await Promise.all(
			unread.map((n) => {
				const orgId = leaves.find((l) => l.tenantId === n.tenantId)?.orgId;
				return orgId
					? api.patch(`/api/notifications/${n.id}?orgId=${orgId}`, { letto: true })
					: Promise.resolve();
			}),
		);
		patchCache((prev) => prev.map((n) => ({ ...n, letto: true })));
		window.dispatchEvent(new CustomEvent('notif:read-all'));
	}, [notifications, leaves, patchCache]);

	const createNotification = useCallback(
		async (data: Omit<DBNotification, 'id' | 'tenantId' | 'createdAt'>, orgId?: string) => {
			const targetOrgId = orgId ?? targetScopes[0]?.orgId;
			if (!targetOrgId) throw new Error('Nessuna configurazione selezionata');
			const created = await api.post<DBNotification>(
				`/api/notifications?orgId=${targetOrgId}`,
				data,
			);
			patchCache((prev) =>
				prev.some((n) => n.id === created.id) ? prev : sortByCreated([created, ...prev]),
			);
			return created;
		},
		[targetScopes, patchCache],
	);

	const deleteNotification = useCallback(
		async (id: string) => {
			const orgId = orgIdForRow(id);
			if (!orgId) return;
			await api.delete(`/api/notifications/${id}?orgId=${orgId}`);
			patchCache((prev) => prev.filter((n) => n.id !== id));
		},
		[orgIdForRow, patchCache],
	);

	const deleteNotifications = useCallback(
		async (ids?: string[]) => {
			if (ids && ids.length > 0) {
				// Group the ids by their org so each bulk delete stays tenant-scoped.
				const byOrg = new Map<string, string[]>();
				for (const id of ids) {
					const orgId = orgIdForRow(id);
					if (!orgId) continue;
					byOrg.set(orgId, [...(byOrg.get(orgId) ?? []), id]);
				}
				await Promise.all(
					[...byOrg.entries()].map(([orgId, orgIds]) =>
						api.delete(`/api/notifications?orgId=${orgId}`, { ids: orgIds }),
					),
				);
				patchCache((prev) => prev.filter((n) => !ids.includes(n.id)));
			} else {
				// Delete-all: one bulk delete per selected scope.
				await Promise.all(
					targetScopes.map((scope) => api.delete(`/api/notifications?orgId=${scope.orgId}`, {})),
				);
				patchCache(() => []);
			}
		},
		[orgIdForRow, targetScopes, patchCache],
	);

	const generateTestNotifications = useCallback(
		async (opts?: { orgId?: string; memberId?: string | null }) => {
			const targetOrgId = opts?.orgId ?? targetScopes[0]?.orgId;
			if (!targetOrgId) throw new Error('Nessuna configurazione selezionata');
			const res = await api.post<{ created: number; notifications: DBNotification[] }>(
				`/api/notifications/test?orgId=${targetOrgId}`,
				{ memberId: opts?.memberId ?? undefined },
			);
			// The generator replaces the previous set, so drop the stale rows before merging.
			patchCache((prev) => {
				const kept = prev.filter((n) => n.canale !== TEST_NOTIFICATION_CANALE);
				return sortByCreated([...res.notifications, ...kept]);
			});
			return res.created;
		},
		[targetScopes, patchCache],
	);

	const deleteTestNotifications = useCallback(
		async (orgId?: string) => {
			const targetOrgId = orgId ?? targetScopes[0]?.orgId;
			if (!targetOrgId) throw new Error('Nessuna configurazione selezionata');
			const res = await api.delete<{ deleted: number }>(
				`/api/notifications/test?orgId=${targetOrgId}`,
			);
			patchCache((prev) => prev.filter((n) => n.canale !== TEST_NOTIFICATION_CANALE));
			return res.deleted;
		},
		[targetScopes, patchCache],
	);

	const unreadCount = notifications.filter((n) => !n.letto).length;

	return {
		notifications,
		isLoading: targetScopes.length > 0 ? isLoading : true,
		error: error
			? error instanceof Error
				? error.message
				: 'Errore nel caricamento delle notifiche'
			: null,
		unreadCount,
		refetch,
		markAsRead,
		markAsUnread,
		markAllAsRead,
		createNotification,
		deleteNotification,
		deleteNotifications,
		generateTestNotifications,
		deleteTestNotifications,
	};
}
